/* eslint-disable max-lines-per-function */
import { Button } from '@/components/ui/button';
import {
  DialogHeader as ShadcnDialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getMonthNameByDate } from '@/utils';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useFormSubmit } from '../useFormSubmit';
import { useRouteParams } from '../useRouteParams';
import { useDialogContext } from '../DialogContext';
import { useNavigateToDate } from './useNavigateToDate';
import * as Sentry from '@sentry/react';
import { YearMonthPicker } from '@/domain/controlling/components/YearMonthPicker';

export function DialogHeader() {
  const { t } = useTranslation();
  const { routeParams } = useRouteParams();
  const { dispatch, zieleForm } = useDialogContext();
  const { navigateToPrevMonth, navigateToCurrentMonth, navigateToNextMonth } =
    useNavigateToDate();

  const { handleSubmit: handleSubmitAndNavigateToNextMonth } = useFormSubmit(
    onSuccessAndNavigateToNextMonth,
    onError,
  );
  const { handleSubmit: handleSubmitAndNavigateToPrevMonth } = useFormSubmit(
    onSuccessAndNavigateToPrevMonth,
    onError,
  );
  const { handleSubmit: handleSubmitAndNavigateToCurrentMonth } = useFormSubmit(
    onSuccessAndNavigateToCurrentMonth,
    onError,
  );
  const { searchParams } = useRouteParams();
  const currentDate = `${searchParams.jahr}-${searchParams.monat}-01`;

  function onSuccessAndNavigateToNextMonth() {
    dispatch({ type: 'reset' });
    dispatch({
      type: 'updateDialogMessage',
      dialogMessage: {
        message: t('domain.controlling.umsatzplanung.ziele.success'),
        type: 'success',
      },
    });
    navigateToNextMonth();
  }

  function onSuccessAndNavigateToPrevMonth() {
    dispatch({ type: 'reset' });
    dispatch({
      type: 'updateDialogMessage',
      dialogMessage: {
        message: t('domain.controlling.umsatzplanung.ziele.success'),
        type: 'success',
      },
    });
    navigateToPrevMonth();
  }

  function onSuccessAndNavigateToCurrentMonth() {
    dispatch({ type: 'reset' });
    dispatch({
      type: 'updateDialogMessage',
      dialogMessage: {
        message: t('domain.controlling.umsatzplanung.ziele.success'),
        type: 'success',
      },
    });
    navigateToCurrentMonth();
  }

  function onError(error: Error) {
    dispatch({
      type: 'updateDialogMessage',
      dialogMessage: {
        message: t(
          'domain.controlling.umsatzplanung.ziele.errors.unknownError',
        ),
        type: 'error',
      },
    });
    Sentry.captureException(error);
  }

  function buildUrl(selectedDate: Date) {
    const year = dayjs(selectedDate).get('year');
    const month = dayjs(selectedDate).get('month') + 1;
    return `/controlling/umsatzplanung/${routeParams.jahr}/${routeParams.monat}/ziele/edit?jahr=${year}&monat=${month}`;
  }

  return (
    <ShadcnDialogHeader>
      <div className="flex flex-col gap-6">
        <DialogTitle className="text-xl font-bold text-neutrals-900">
          {t('domain.controlling.umsatzplanung.ziele.dialog.title')}
        </DialogTitle>
        <div className="flex items-center gap-4">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={(e) => {
              if (zieleForm.hasChanges) {
                handleSubmitAndNavigateToCurrentMonth(e);
              } else {
                dispatch({ type: 'reset' });
                navigateToCurrentMonth();
              }
            }}>
            {t('general.date.today')}
          </Button>
          <div className="flex items-center">
            <Button
              size="icon"
              variant="icon"
              className="text-neutrals-700 disabled:text-neutrals-500"
              onClick={(e) => {
                if (zieleForm.hasChanges) {
                  handleSubmitAndNavigateToPrevMonth(e);
                } else {
                  dispatch({ type: 'reset' });
                  navigateToPrevMonth();
                }
              }}>
              <FontAwesomeIcon className="text-xl" icon={faChevronLeft} />
            </Button>
            <Button
              size="icon"
              variant="icon"
              className="text-neutrals-700 disabled:text-neutrals-500"
              onClick={(e) => {
                if (zieleForm.hasChanges) {
                  handleSubmitAndNavigateToNextMonth(e);
                } else {
                  dispatch({ type: 'reset' });
                  navigateToNextMonth();
                }
              }}>
              <FontAwesomeIcon className="text-xl" icon={faChevronRight} />
            </Button>
          </div>
          <YearMonthPicker
            selectedDate={new Date(currentDate)}
            buildUrl={buildUrl}>
            <Button className="font-bold" variant="ghost">
              {getMonthNameByDate(dayjs(currentDate).format('YYYY-MM-DD'))}
            </Button>
          </YearMonthPicker>
        </div>
      </div>
    </ShadcnDialogHeader>
  );
}
